import React, { useEffect, useState } from "react";
import { hubExt } from "../api.js";
import { toast } from "react-toastify";
import { useIsMobile } from "../hooks/useIsMobile.js";

const PRIORITY_STYLE = {
  low:    { bg: "#f8fafc", color: "#64748b", label: "Scăzută" },
  medium: { bg: "#fffbeb", color: "#d97706", label: "Medie" },
  high:   { bg: "#fff1f2", color: "#e11d48", label: "Ridicată" },
};

const STATUS_STYLE = {
  open:        { bg: "#fffbeb", color: "#d97706", label: "Deschis" },
  in_progress: { bg: "#eff6ff", color: "#7c3aed", label: "În lucru" },
  resolved:    { bg: "#f0fdf4", color: "#16a34a", label: "Rezolvat" },
};

function ChatBubble({ author, text, name, date }) {
  const isAdmin = author === "admin";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isAdmin ? "flex-end" : "flex-start" }}>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>
        {name} · {date ? new Date(date).toLocaleString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
      </div>
      <div style={{
        maxWidth: "85%", padding: "10px 14px", borderRadius: isAdmin ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        background: isAdmin ? "#1d4ed8" : "#f1f5f9",
        color: isAdmin ? "white" : "#1e293b",
        fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap",
      }}>{text}</div>
    </div>
  );
}

export default function TicketsPage() {
  const isMobile = useIsMobile();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await hubExt.getAllTickets();
      setTickets(data.tickets || []);
    } catch { toast.error("Eroare la încărcare tickete."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? tickets : tickets.filter(t => t.status === filter);

  const handleSelect = (t) => {
    setSelected(t);
    setReply(t.adminReply || "");
  };

  const handleReply = async () => {
    if (!reply.trim() || !selected) return;
    setSaving(true);
    try {
      await hubExt.replyTicket(selected.userId, selected._id, reply.trim());
      toast.success("Răspuns trimis!");
      await load();
      setSelected(prev => ({ ...prev, adminReply: reply, status: "in_progress" }));
    } catch { toast.error("Eroare la trimitere răspuns."); }
    finally { setSaving(false); }
  };

  const handleStatus = async (status) => {
    if (!selected) return;
    try {
      await hubExt.setTicketStatus(selected.userId, selected._id, status);
      toast.success("Status actualizat.");
      await load();
      setSelected(prev => ({ ...prev, status }));
    } catch { toast.error("Eroare la actualizare status."); }
  };

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === "open").length,
    in_progress: tickets.filter(t => t.status === "in_progress").length,
    resolved: tickets.filter(t => t.status === "resolved").length,
  };

  return (
    <div style={{ padding: isMobile ? "16px" : "32px 36px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.4px" }}>Tickete suport</h1>
        <p style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>Mesaje primite de la utilizatorii portalului app.orderly.ro</p>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[["all","Toate"], ["open","Deschise"], ["in_progress","În lucru"], ["resolved","Rezolvate"]].map(([val, lbl]) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: "7px 16px", borderRadius: 8, border: "1.5px solid",
            borderColor: filter === val ? "#1d4ed8" : "#e2e8f0",
            background: filter === val ? "#eff6ff" : "white",
            color: filter === val ? "#1e3a8a" : "#64748b",
            fontWeight: 600, fontSize: 13, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {lbl}
            <span style={{
              background: filter === val ? "#1d4ed8" : "#e2e8f0",
              color: filter === val ? "white" : "#64748b",
              borderRadius: 100, fontSize: 11, fontWeight: 700,
              padding: "1px 7px",
            }}>{counts[val]}</span>
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected && !isMobile ? "1fr 420px" : "1fr", gap: 20 }}>
        {/* List */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Se încarcă...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: "#94a3b8", background: "white", borderRadius: 14, border: "1px solid #f1f5f9" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📬</div>
              <div style={{ fontWeight: 700, color: "#475569", marginBottom: 4 }}>Niciun ticket</div>
              <div style={{ fontSize: 13 }}>Nu există tickete cu filtrul selectat.</div>
            </div>
          ) : filtered.map(t => {
            const ss = STATUS_STYLE[t.status] || STATUS_STYLE.open;
            const ps = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium;
            const isActive = selected?._id === t._id;
            return (
              <div key={t._id} onClick={() => handleSelect(t)} style={{
                background: "white", border: `1.5px solid ${isActive ? "#1d4ed8" : "#f1f5f9"}`,
                borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                transition: "border-color .15s, box-shadow .15s",
                boxShadow: isActive ? "0 0 0 3px rgba(99,102,241,.1)" : "none",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
                      <strong style={{ color: "#334155" }}>{t.restaurantName}</strong> · {t.userFullName} · {t.userEmail}
                    </div>
                    <div style={{ fontSize: 13, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.message}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 100, background: ss.bg, color: ss.color }}>{ss.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 100, background: ps.bg, color: ps.color }}>{ps.label}</span>
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>{new Date(t.createdAt).toLocaleDateString("ro-RO")}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel — chat thread */}
        {selected && (
          <div style={isMobile ? {
            position: "fixed", inset: 0, zIndex: 500,
            background: "white", display: "flex", flexDirection: "column",
          } : {
            background: "white", borderRadius: 14, border: "1.5px solid #f1f5f9",
            display: "flex", flexDirection: "column", position: "sticky", top: 20, maxHeight: "82vh",
          }}>
            {/* Header */}
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <h3 style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", margin: 0 }}>{selected.subject}</h3>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                {selected.restaurantName} · {selected.userFullName}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                {["open", "in_progress", "resolved"].map(s => {
                  const st = STATUS_STYLE[s];
                  return (
                    <button key={s} onClick={() => handleStatus(s)} style={{
                      padding: "4px 10px", borderRadius: 7, border: `1.5px solid ${selected.status === s ? st.color : "#e2e8f0"}`,
                      background: selected.status === s ? st.bg : "white",
                      color: selected.status === s ? st.color : "#64748b",
                      fontSize: 11, fontWeight: 700, cursor: "pointer",
                    }}>{st.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Chat messages */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
              {/* First user message */}
              <ChatBubble author="user" text={selected.message} name={selected.userFullName} date={selected.createdAt} />

              {/* Replies */}
              {(selected.replies || []).map((r, i) => (
                <ChatBubble key={i} author={r.author} text={r.text} name={r.author === "admin" ? "Echipa Orderly" : selected.userFullName} date={r.createdAt} />
              ))}
            </div>

            {/* Reply input */}
            <div style={{ padding: "14px 20px", borderTop: "1px solid #f1f5f9" }}>
              <textarea value={reply} onChange={e => setReply(e.target.value)}
                placeholder="Scrie un răspuns..." rows={3}
                style={{ width: "100%", borderRadius: 10, border: "1.5px solid #e2e8f0", padding: "10px 14px", fontSize: 13, fontFamily: "inherit", resize: "none", outline: "none", boxSizing: "border-box" }}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleReply(); }}
              />
              <button onClick={handleReply} disabled={saving || !reply.trim()} style={{
                marginTop: 8, width: "100%", height: 40, background: "#1d4ed8", color: "white",
                border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: saving ? "not-allowed" : "pointer",
                opacity: saving || !reply.trim() ? 0.6 : 1,
              }}>{saving ? "Se trimite..." : "Trimite răspuns · ⌘↵"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
